import { css } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { Icon, IconName } from '@frontend/icons';
import { theme } from '@frontend/theme';
import { AccordionMultiSelect, ModalControlModalProps, NakedButton, Tray, useFormField } from '@frontend/design-system';

type FilterFieldConfig = {
  key: string;
  title: string;
  iconName: IconName;
  items: { value: string; label: string }[];
  hideSearch?: boolean;
  clearLabel?: string;
  /**
   * Minimum container width (px) above which this filter is rendered inline
   * on the row. Below this width the filter moves to the tray.
   */
  minInlineWidth?: number;
};

export type ChecklistFilterConfig = FilterFieldConfig & {
  type: 'checklist';
  fieldProps: ReturnType<typeof useFormField<'checklist'>>;
  selectAllLabel?: string;
  clearLabel?: string;
};

export type RadioFilterConfig = FilterFieldConfig & {
  type: 'radio';
  fieldProps: ReturnType<typeof useFormField<'radio'>>;
  resetValue?: string;
};

export type FilterConfig = ChecklistFilterConfig | RadioFilterConfig;

type FiltersTrayProps = {
  modalProps: ModalControlModalProps;
  filters: FilterConfig[];
  containerName: string;
};

export const FiltersTray = ({ modalProps, filters, containerName }: FiltersTrayProps) => {
  const { t } = useTranslation('schedule');

  const eligibleFilters = filters.filter((f) => f.items.length > 0);

  const handleReset = () => {
    eligibleFilters.forEach((filter) => {
      if (filter.type === 'checklist') {
        filter.fieldProps.onChange({ name: filter.key, value: [] });
      } else {
        filter.fieldProps.onChange({ name: filter.key, value: filter.resetValue ?? '' });
      }
    });
    modalProps.onClose();
  };

  return (
    <Tray width='medium' {...modalProps}>
      <Tray.Header
        Buttons={
          <NakedButton onClick={modalProps.onClose} css={styles.closeButton} aria-label={t('Close filters')}>
            <Icon name='x' size={20} color='default' />
          </NakedButton>
        }
      >
        {t('Filters')}
      </Tray.Header>

      <Tray.Body css={styles.body}>
        <AccordionMultiSelect isMulti>
          {eligibleFilters.map((filter) => {
            // When minInlineWidth is set, hide this tray item via container query
            // once the container is wide enough to show the filter inline.
            const trayItemCss =
              filter.minInlineWidth != null && filter.minInlineWidth > 0
                ? css`
                    @container ${containerName} (min-width: ${filter.minInlineWidth}px) {
                      display: none;
                    }
                  `
                : undefined;

            if (filter.type === 'radio') {
              return (
                <div key={filter.key} css={trayItemCss}>
                  <AccordionMultiSelect.Item
                    title={filter.title}
                    iconName={filter.iconName}
                    items={filter.items}
                    fieldProps={{ ...filter.fieldProps, name: filter.key }}
                    hideSearch={filter.hideSearch ?? true}
                    isSingleSelect
                    hideClear
                  />
                </div>
              );
            }

            // type === 'checklist'
            return (
              <div key={filter.key} css={trayItemCss}>
                <AccordionMultiSelect.Item
                  title={filter.title}
                  iconName={filter.iconName}
                  items={filter.items}
                  fieldProps={{ ...filter.fieldProps, name: filter.key }}
                  hideSearch={filter.hideSearch ?? false}
                  selectAllLabel={filter.selectAllLabel ?? t('Select All')}
                  clearLabel={filter.clearLabel ?? t('Clear')}
                />
              </div>
            );
          })}
        </AccordionMultiSelect>
      </Tray.Body>

      <Tray.Actions
        primaryLabel={t('Done')}
        onPrimaryClick={modalProps.onClose}
        secondaryLabel={t('Reset')}
        onSecondaryClick={handleReset}
      />
    </Tray>
  );
};

const styles = {
  closeButton: css`
    display: flex;
    align-items: center;
    justify-content: center;
  `,
  body: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(2)};
    padding: ${theme.spacing(2, 0)};
  `,
};
